import { useId, useRef, useState } from 'react';
import { PlusIcon } from 'lucide-react';

import { cn } from '../cn';
import { ErrorNotice, Field, TextArea, TextInput } from './primitives';
import type { ProfileRowColumn } from '../brand-profile-fields';

/**
 * The controls a long descriptive form needs and the base primitives do not
 * provide: a one-per-line list, a colour picker, repeatable rows and an image
 * upload. Each pairs a label with a description above the control, because on
 * a form filled in from scratch the explanation has to be readable before
 * someone starts typing.
 */

// ─── ListField ──────────────────────────────────────────────────────────────

export interface ListFieldProps {
  readonly label: string;
  readonly description?: string;
  /** Sits on the label's row — a reset link, say. */
  readonly action?: React.ReactNode;
  readonly value: readonly string[];
  readonly onChange: (value: string[]) => void;
  readonly placeholder?: string;
  readonly rows?: number;
  readonly disabled?: boolean;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
}

/**
 * A list edited as one entry per line.
 *
 * A textarea rather than a row-at-a-time builder: this content arrives pasted
 * out of a deck or an intranet page, and a textarea takes that in one gesture
 * where a builder fights it.
 *
 * @param props - Label, description, the list and its change handler.
 * @returns The field.
 */
export function ListField({
  label,
  description,
  action,
  value,
  onChange,
  placeholder,
  rows = 4,
  disabled = false,
  guide,
}: ListFieldProps): React.ReactElement {
  const id = useId();
  return (
    <Field
      guide={guide}
      label={label}
      htmlFor={id}
      {...(description === undefined ? {} : { hint: description })}
      {...(action === undefined ? {} : { action })}
    >
      <TextArea
        id={id}
        rows={rows}
        disabled={disabled}
        value={value.join('\n')}
        placeholder={placeholder}
        onChange={(event) =>
          onChange(
            event.target.value
              .split('\n')
              // Blank lines are how people space out a list while typing, so
              // they are dropped on the way out rather than stored as entries.
              .map((line) => line.trim())
              .filter((line) => line.length > 0),
          )
        }
      />
    </Field>
  );
}

// ─── ColorField ─────────────────────────────────────────────────────────────

export interface ColorFieldProps {
  readonly label: string;
  readonly description?: string;
  /** Sits on the label's row — a reset link, say. */
  readonly action?: React.ReactNode;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  /**
   * The #rrggbb the swatch shows, when the typed value is some other way of
   * writing a color ("red", "#f00"). Defaults to the value itself.
   */
  readonly swatch?: string;
}

/**
 * A colour, pickable or typed as hex.
 *
 * Both controls are offered because both are used: the swatch for choosing,
 * the hex box for pasting the exact value out of a brand guideline.
 *
 * @param props - Label, description, the colour and its change handler.
 * @returns The field.
 */
export function ColorField({
  label,
  description,
  action,
  value,
  onChange,
  disabled = false,
  swatch,
}: ColorFieldProps): React.ReactElement {
  const id = useId();
  return (
    <Field
      label={label}
      htmlFor={id}
      {...(description === undefined ? {} : { hint: description })}
      {...(action === undefined ? {} : { action })}
    >
      <div className="mt-1.5 flex items-center gap-2">
        <input
          id={id}
          type="color"
          value={swatch ?? value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className="size-[43px] cursor-pointer rounded-portal-input border border-[#cad3df] bg-white p-1 disabled:opacity-40"
        />
        <TextInput
          aria-label={`${label} hex value`}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className="!mt-0 w-32 font-mono"
        />
      </div>
    </Field>
  );
}

// ─── RepeatableRows ─────────────────────────────────────────────────────────

/** One row: a value per column key. */
export type RepeatableRow = Record<string, string>;

export interface RepeatableRowsProps {
  readonly label: string;
  readonly description?: string;
  readonly columns: readonly ProfileRowColumn[];
  readonly value: readonly RepeatableRow[];
  readonly onChange: (value: RepeatableRow[]) => void;
  readonly addLabel?: string;
  readonly disabled?: boolean;
}

/**
 * A repeating group of fields — a leadership quote and who said it, a term and
 * its meaning, an asset and its link.
 *
 * Rows rather than free text because each entry has parts the reader needs
 * kept apart: a quote without attribution cannot go on a slide.
 *
 * @param props - Label, description, column shape, rows and change handler.
 * @returns The field.
 */
export function RepeatableRows({
  label,
  description,
  columns,
  value,
  onChange,
  addLabel = 'Add another',
  disabled = false,
}: RepeatableRowsProps): React.ReactElement {
  /** Empty row matching the column shape. */
  function blankRow(): RepeatableRow {
    return Object.fromEntries(columns.map((column) => [column.key, '']));
  }

  return (
    <div>
      <span className="block text-portal-tiny font-bold">{label}</span>
      {description === undefined ? null : (
        <p className="mt-0.5 text-portal-micro text-portal-muted">{description}</p>
      )}

      <div className="mt-2 grid gap-3">
        {value.map((row, index) => (
          <div
            // Index-keyed deliberately: rows have no id, and reordering is not
            // offered — only add, edit and remove.
            key={index}
            className="grid gap-2 rounded-portal-card border border-portal-line p-3"
          >
            <div
              className={cn(
                'grid gap-2',
                columns.filter((column) => column.multiline !== true).length > 1 &&
                  'sm:grid-cols-2',
              )}
            >
              {columns
                .filter((column) => column.multiline !== true)
                .map((column) => (
                  <TextInput
                    key={column.key}
                    aria-label={column.label}
                    placeholder={column.placeholder ?? column.label}
                    disabled={disabled}
                    value={row[column.key] ?? ''}
                    onChange={(event) =>
                      onChange(
                        value.map((current, i) =>
                          i === index ? { ...current, [column.key]: event.target.value } : current,
                        ),
                      )
                    }
                    className="!mt-0"
                  />
                ))}
            </div>

            {columns
              .filter((column) => column.multiline === true)
              .map((column) => (
                <TextArea
                  key={column.key}
                  rows={3}
                  aria-label={column.label}
                  placeholder={column.placeholder ?? column.label}
                  disabled={disabled}
                  value={row[column.key] ?? ''}
                  onChange={(event) =>
                    onChange(
                      value.map((current, i) =>
                        i === index ? { ...current, [column.key]: event.target.value } : current,
                      ),
                    )
                  }
                  className="!mt-0"
                />
              ))}

            {disabled ? null : (
              <div>
                <button
                  type="button"
                  onClick={() => onChange(value.filter((_, i) => i !== index))}
                  className="text-portal-micro font-semibold text-rose-600 hover:underline"
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        ))}

        {disabled ? null : (
          <div>
            <button
              type="button"
              onClick={() => onChange([...value, blankRow()])}
              className="text-portal-small font-bold text-brand underline-offset-2 hover:underline"
            >
              <PlusIcon aria-hidden className="inline size-3.5 shrink-0" /> {addLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ImageUploadField ───────────────────────────────────────────────────────

export interface ImageUploadFieldProps {
  readonly label: string;
  readonly description?: string;
  /** Sits on the label's row — a reset link, say. */
  readonly action?: React.ReactNode;
  /** The current image URL. Empty when none. */
  readonly value: string;
  /** Called with the new URL after upload, or '' when cleared. */
  readonly onChange: (url: string) => void;
  /**
   * Colour to show the preview against. Defaults to the page background; pass
   * the surface this image actually sits on — a logo drawn for a dark sidebar
   * is invisible previewed on white, which is exactly when someone needs to
   * see it.
   */
  readonly previewBackground?: string;
  /**
   * Uploads the chosen file and resolves to its URL. Omit to offer only the
   * paste-a-URL path — the package holds no HTTP client of its own, so each
   * app passes the upload it is allowed to perform.
   */
  readonly upload?: (file: File) => Promise<string>;
  readonly disabled?: boolean;
  /** Anchors a tour step: rendered as `data-guide` on the root element. */
  readonly guide?: string | undefined;
}

/**
 * File types offered in the picker. SVG is absent on purpose — see the portal
 * media route: an SVG is a document that executes script when opened directly.
 */
const IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

/**
 * An image, uploaded or pasted as a URL.
 *
 * Upload-first — someone editing their own branding has a file, not a hosted
 * URL — but the URL box stays available for artwork already on a CDN, and the
 * stored value is a URL either way, so nothing downstream changes.
 *
 * @param props - Label, description, the URL, its change handler and the upload.
 * @returns The field.
 */
export function ImageUploadField({
  label,
  description,
  action,
  value,
  onChange,
  previewBackground,
  upload,
  disabled = false,
  guide,
}: ImageUploadFieldProps): React.ReactElement {
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUrl, setShowUrl] = useState(false);

  /**
   * Uploads the chosen file and stores the resulting URL.
   *
   * @param event - The file input change.
   */
  async function onFile(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    // Reset the input so choosing the same file again still fires onChange.
    event.target.value = '';
    if (!file || !upload) return;
    setError(null);
    setBusy(true);
    try {
      onChange(await upload(file));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload that image.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-guide={guide}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="block text-portal-tiny font-bold">{label}</span>
        {action}
      </div>
      {description === undefined ? null : (
        <p className="mt-0.5 text-portal-micro text-portal-muted">{description}</p>
      )}

      <div className="mt-2 flex items-center gap-4">
        <div
          className="grid size-16 shrink-0 place-items-center overflow-hidden rounded-portal-input border border-portal-line bg-portal-bg"
          {...(previewBackground === undefined
            ? {}
            : { style: { background: previewBackground } })}
        >
          {value === '' ? (
            <span className="text-portal-micro text-portal-muted" aria-hidden="true">
              No image
            </span>
          ) : (
            <img src={value} alt={`${label} preview`} className="size-full object-contain" />
          )}
        </div>

        <div className="flex flex-col items-start gap-1.5">
          {disabled ? null : (
            <div className="flex items-center gap-2">
              {upload === undefined ? null : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={busy}
                  className="h-9 rounded-portal-button border border-[#cad3df] bg-white px-3 text-portal-small font-bold disabled:opacity-50"
                >
                  {busy ? 'Uploading…' : value === '' ? 'Upload image' : 'Replace'}
                </button>
              )}
              {value === '' ? null : (
                <button
                  type="button"
                  onClick={() => onChange('')}
                  disabled={busy}
                  className="h-9 rounded-portal-button px-2 text-portal-small font-bold text-rose-600 disabled:opacity-50"
                >
                  Remove
                </button>
              )}
            </div>
          )}

          {disabled ? null : (
            <button
              type="button"
              onClick={() => setShowUrl((shown) => !shown)}
              className="text-portal-micro text-portal-muted underline-offset-2 hover:underline"
            >
              {showUrl ? 'Hide URL field' : 'Or paste a URL'}
            </button>
          )}
        </div>

        <input
          ref={fileRef}
          type="file"
          accept={IMAGE_ACCEPT}
          onChange={(event) => void onFile(event)}
          className="sr-only"
        />
      </div>

      {showUrl && !disabled ? (
        <TextInput
          type="url"
          // The field's label is a plain heading over both paths, so the URL
          // box names itself; without this a screen reader announced an
          // unlabeled edit box.
          aria-label={`${label} URL`}
          value={value}
          placeholder="https://…"
          onChange={(event) => onChange(event.target.value)}
        />
      ) : null}

      {error === null ? null : (
        <div className="mt-2">
          <ErrorNotice inline title="Upload failed" message={error} />
        </div>
      )}
    </div>
  );
}
